import { useMemo, useRef, useState } from 'react';
import { UMMap, UMMapControls, type UMMapHandle } from '../../urbanmap/_components';
import { BuildingCard } from './BuildingCard';
import { CONSUMPTION_CLASSES, boundsOf, buildingConsumption, cloneRatios, fmt, totalConsumption } from './domain';
import { useProject, useUpdateProject } from './hooks';
import { EMPTY, buildingsFC, projectLayers, ringFC } from './mapLayers';
import { ProjectHeader, ProjectRail } from './ProjectHeader';
import { SettingsPanel, type SettingsDraft } from './SettingsPanel';
import { SyncPanel } from './SyncPanel';
import { PC, UMR, caption, card } from './tokens';
import type { LngLat, PlanProject, Referential } from './types';

interface Props {
  projectId: string;
  referential: Referential;
  onBack: () => void;
  flash: (msg: string) => void;
}

const toDraft = (p: PlanProject): SettingsDraft => ({ name: p.name, ratios: cloneRatios(p.ratios), dju: { ...p.dju } });
const same = (a: SettingsDraft, b: SettingsDraft) => a.name.trim() === b.name.trim() && JSON.stringify(a.ratios) === JSON.stringify(b.ratios) && JSON.stringify(a.dju) === JSON.stringify(b.dju);

export function ProjectView({ projectId, referential, onBack, flash }: Props) {
  const { data, isLoading, isError } = useProject(projectId);
  const update = useUpdateProject(projectId);
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<'settings' | 'sync' | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [revision, setRevision] = useState(0);
  const [justSaved, setJustSaved] = useState(false);

  const project = data?.project;
  const saved = useMemo(() => (project ? toDraft(project) : null), [project]);
  const dirty = !!(draft && saved && !same(draft, saved));
  const params = panel === 'settings' && draft ? draft : saved;
  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const changedIds = useMemo(() => new Set(data?.umChangedIds ?? []), [data]);

  const outlines: LngLat[][] = useMemo(() => {
    if (!project) return [];
    if (project.perimeter.kind === 'drawn') return [project.perimeter.polygon];
    return project.perimeter.communes.map((c) => referential.communeOutlines[c]).filter((r): r is LngLat[] => !!r);
  }, [project, referential.communeOutlines]);

  const sources = useMemo(() => ({
    buildings: params ? buildingsFC(buildings, (b) => ({ conso: Math.round(buildingConsumption(b, params.ratios, params.dju).corrected * 10) / 10 })) : EMPTY,
    outline: ringFC(outlines),
  }), [buildings, params, outlines]);
  const pickedB = picked ? buildings.find((b) => b.id === picked) ?? null : null;
  const layers = useMemo(() => projectLayers(pickedB?.num ?? null), [pickedB]);
  const total = params ? totalConsumption(buildings, params.ratios, params.dju) : 0;

  if (isLoading) return <div style={{ padding: 24 }}>Chargement du projet…</div>;
  if (isError || !data || !project || !params || !saved) {
    return <div style={{ padding: 24 }}>Projet introuvable. <button type="button" onClick={onBack}>Retour aux projets</button></div>;
  }

  const initialBounds = outlines.length ? boundsOf(outlines.flat()) : referential.bounds;
  const openSettings = () => {
    if (panel === 'settings') { closeSettings(); return; }
    setDraft(toDraft(project)); setJustSaved(false); setRevision((r) => r + 1); setPanel('settings');
  };
  const closeSettings = () => {
    if (dirty) flash('Modifications non enregistrées abandonnées');
    setPanel(null); setDraft(null);
  };
  const save = () => {
    if (!draft) return;
    update.mutate({ name: draft.name.trim(), ratios: draft.ratios, dju: draft.dju }, {
      onSuccess: (d) => { setDraft(toDraft(d.project)); setJustSaved(true); flash('Paramètres enregistrés · consommations recalculées'); },
      onError: () => flash('Échec de l’enregistrement, réessayez'),
    });
  };

  return (
    <div data-comment-view={`project-${projectId}`} style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <ProjectRail onProjects={() => { if (dirty) flash('Modifications non enregistrées abandonnées'); onBack(); }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <ProjectHeader
          project={project}
          umChanges={data.umChanges}
          settingsOpen={panel === 'settings'}
          syncOpen={panel === 'sync'}
          onSettings={openSettings}
          onSync={() => { if (panel === 'settings') closeSettings(); setPanel(panel === 'sync' ? null : 'sync'); }}
        />
        <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
          <main data-comment-anchor="project-map" style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <UMMap
              ref={mapRef}
              sources={sources}
              layers={layers}
              initialBounds={initialBounds}
              interactiveLayerIds={['pc-prj-building']}
              onFeatureClick={(_, f) => setPicked(String(f.properties?.fid))}
              onMapClick={() => setPicked(null)}
            />
            <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
            <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 10, ...card, width: 230, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <strong style={{ fontSize: 14 }}>Consommation corrigée</strong>
              <span style={caption}>MWh/an par bâtiment</span>
              {CONSUMPTION_CLASSES.map((c) => (
                <span key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 18, height: 12, background: c.color, border: `1px solid ${UMR.border}` }} />
                  {c.label}
                </span>
              ))}
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 18, height: 0, borderTop: `2px dashed ${PC.outline}` }} />
                Contour du périmètre
              </span>
              <span style={{ ...caption, borderTop: `1px solid ${UMR.borderSubtle}`, paddingTop: 6 }}>
                Total : <strong style={{ color: UMR.textStrong }}>{fmt(total / 1000, 1)} GWh/an</strong>
                {panel === 'settings' && dirty && <><br />Aperçu des paramètres non enregistrés</>}
              </span>
            </div>
          </main>
          {pickedB && (
            <BuildingCard building={pickedB} ratios={params.ratios} dju={params.dju} changedInUM={changedIds.has(pickedB.id)} preview={panel === 'settings' && dirty} onClose={() => setPicked(null)} />
          )}
          {panel === 'settings' && draft && (
            <div data-comment-view="panel-settings" style={{ display: 'contents' }}>
            <SettingsPanel
              project={project}
              buildings={buildings}
              referential={referential}
              draft={draft}
              onDraftChange={(p) => { setJustSaved(false); setDraft((d) => (d ? { ...d, ...p } : d)); }}
              dirty={dirty}
              saving={update.isPending}
              justSaved={justSaved}
              revision={revision}
              onSave={save}
              onCancel={() => { setDraft(toDraft(project)); setRevision((r) => r + 1); }}
              onClose={closeSettings}
            />
            </div>
          )}
          {panel === 'sync' && (
            <div data-comment-view="panel-sync" style={{ display: 'contents' }}>
              <SyncPanel projectId={projectId} onClose={() => setPanel(null)} onDone={flash} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
